import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { CustomColorsRef, IconSizes } from 'types/general'

import IconButton from './iconButton/IconButton'
import Loading from './loading/Loading'
import NoImageSample from './NoImageSample'

type SlideProps = {
  images: string[]
  hiddePreviews?: boolean
  hiddeList?: boolean
  customSize?: boolean
  hiddeButtons?: boolean
  next?: number
  prev?: number
  setCurrentImage?: (index: number) => void
  customImageClass?: string
  active?: boolean
  buttonClassName?: string
}

const Slide = ({
  images,
  hiddePreviews,
  hiddeList,
  hiddeButtons,
  customSize,
  next,
  prev,
  setCurrentImage,
  customImageClass,
  active,
  buttonClassName,
}: SlideProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const previewContainerRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const startXRef = useRef(0)
  const scrollLeftRef = useRef(0)
  const [isHovered, setIsHovered] = useState(false)

  const prevImageIndex = () => {
    return currentIndex === 0 ? images.length - 1 : currentIndex - 1
  }

  const nextImageIndex = () => {
    return currentIndex === images.length - 1 ? 0 : currentIndex + 1
  }

  const handlePrev = () => {
    setCurrentIndex(prevImageIndex())
  }

  const handleNext = () => {
    setCurrentIndex(nextImageIndex())
  }

  useEffect(() => {
    if (prev && active) {
      setCurrentIndex(prevImageIndex())
    }
  }, [prev])

  useEffect(() => {
    if (next && active) {
      setCurrentIndex(nextImageIndex())
    }
  }, [next])

  useEffect(() => {
    if (previewContainerRef.current) {
      const previewContainer = previewContainerRef.current
      const selectedImage = previewContainer.children[
        currentIndex
      ] as HTMLDivElement
      if (selectedImage) {
        const containerWidth = previewContainer.offsetWidth
        const imageWidth = selectedImage.offsetWidth
        const imageOffsetLeft = selectedImage.offsetLeft

        previewContainer.scrollLeft =
          imageOffsetLeft - containerWidth / 2 + imageWidth / 2
      }
    }

    setCurrentImage?.(currentIndex)
  }, [currentIndex])

  const handleMouseDown = (e: React.MouseEvent) => {
    isDraggingRef.current = true
    startXRef.current = e.pageX - (previewContainerRef.current?.offsetLeft || 0)
    scrollLeftRef.current = previewContainerRef.current?.scrollLeft || 0
    previewContainerRef.current?.classList.add('dragging')
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingRef.current) return
    e.preventDefault()
    const x = e.pageX - (previewContainerRef.current?.offsetLeft || 0)
    const walk = (x - startXRef.current) * 1.5
    if (previewContainerRef.current) {
      previewContainerRef.current.scrollLeft = scrollLeftRef.current - walk
    }
  }

  const handleMouseUp = () => {
    isDraggingRef.current = false
    previewContainerRef.current?.classList.remove('dragging')
  }

  const handleMouseLeave = () => {
    isDraggingRef.current = false
    previewContainerRef.current?.classList.remove('dragging')
  }

  if (!images) return <Loading />

  return (
    <div className="w-full mx-auto h-full">
      <div className="relative w-full flex flex-col h-full">
        <div
          className="relative flex justify-center items-center flex-1"
          onMouseEnter={() => {
            setIsHovered(true)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
          }}
        >
          {!hiddePreviews && (
            <img
              src={images[prevImageIndex()]}
              alt={`Slide ${prevImageIndex()}`}
              className="lg:w-56 lg:h-44 object-cover rounded-md opacity-50 md:w-36 md:h-28 sm:w-32 sm:h-24 sm:block hidden"
            />
          )}
          {images[currentIndex] ? (
            <img
              src={images[currentIndex]}
              alt={`Slide ${currentIndex}`}
              className={
                customSize
                  ? customImageClass
                  : classNames(
                      'w-[28rem] max-w-[28rem] h-[28rem] min-h-[28rem] max-h-[28rem] object-cover rounded',
                      customImageClass
                    )
              }
            />
          ) : (
            <NoImageSample />
          )}
          {!hiddePreviews && (
            <img
              src={images[nextImageIndex()]}
              alt={`Slide ${nextImageIndex()}`}
              className="lg:w-56 lg:h-44 object-cover rounded-md opacity-50 md:w-36 md:h-28 sm:w-32 sm:h-24 sm:block hidden"
            />
          )}

          {!hiddeButtons && (
            <div>
              <div
                className={classNames(
                  {
                    'opacity-100': isHovered,
                    'opacity-10': !isHovered,
                  },
                  buttonClassName,
                  'absolute top-1/2 left-0 mx-1 bg-white bg-opacity-50 rounded flex items-center'
                )}
              >
                <IconButton
                  icon="regular-chevron-left"
                  size={IconSizes.XXL}
                  color={CustomColorsRef.Primary}
                  hoverColor={CustomColorsRef.PrimaryDark}
                  handleClick={handlePrev}
                />
              </div>
              <div
                className={classNames(
                  {
                    'opacity-100': isHovered,
                    'opacity-10': !isHovered,
                  },
                  buttonClassName,
                  'absolute top-1/2 right-0 mx-1 bg-white bg-opacity-50 rounded flex items-center'
                )}
              >
                <IconButton
                  icon="regular-chevron-right"
                  size={IconSizes.XXL}
                  color={CustomColorsRef.Primary}
                  hoverColor={CustomColorsRef.PrimaryDark}
                  handleClick={handleNext}
                />
              </div>
            </div>
          )}
        </div>

        {!hiddeList && (
          <div
            className="flex justify-center mt-2 space-x-2 overflow-x-auto scrollbar-hide scrollbar-thin"
            ref={previewContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: isDraggingRef.current ? 'grabbing' : 'grab' }}
          >
            {images.map((image, index) => (
              <div key={index}>
                <img
                  src={image}
                  alt={`Preview ${index}`}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-16 h-16 min-w-16 min-h-16 object-cover rounded-md opacity-90 cursor-pointer transition-all duration-300 ${
                    index === currentIndex
                      ? 'border-2 border-imovPrimary'
                      : 'border'
                  }`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Slide
